import axios from "axios";
import { eventBus } from "@/main";
import moment from "moment";
import isDate from "lodash/isDate";
import {
  PERMISSIONS,
  PERMISSION_MAP,
  ASSIGNABLE,
  ROLE_KEYS,
  BRANCH_ROLE_KEYS,
  CENTRAL_ROLE_KEYS,
} from "@/constants/roles";

const moduleUser = {
  namespaced: true,
  state: {
    userInfo: null,
    branchInfo: null,
    branchPath: "",

    // linkedWithKtbPaotang: false,

    clinicList: [],
    doctorList: [],
    userList: [],
    userListIncludDeleted: [],

    paymentMethodList: [],
    paymentMethodCategories: [],

    // Caching and request deduplication
    lastCheckTime: null,
    isCheckingLogin: false,
    checkLoginPromise: null,

    // Phase 1C: clinic-roles permission context.
    // Populated by loadPermissionContext after login / on 403 refresh.
    // Shape: { uid, scopes: [{ type: 'branch'|'clinic_wide'|'branch_subset',
    //                          clinicId, branchId?, branchIds?, roleIds: [..] }, ...] }
    permissionContext: null,
    // Phase 1.5: which clinicURL the active permissionContext was loaded for,
    // and which clinics the user holds a central role in (HQ entry + switcher).
    permissionContextClinicUrl: null,
    centralClinicCodes: [],
    // Phase 1.5: clinic objects from GET /user/central-clinics
    // [{ clinicId, code, name, roleKeys, branchIds }]
    centralClinics: [],
  },
  mutations: {
    setUserInfo(state, data) {
      state.userInfo = data ? data : null;
    },
    setBranchInfo(state, data) {
      if (data && data.clinic) {
        state.branchInfo = data;
        state.branchPath = `${data.clinic.code}/${data.code}`;
        // state.linkedWithKtbPaotang = data.linkedWithKtbPaotang;
      } else {
        state.branchInfo = null;
        state.branchPath = "";
        // state.linkedWithKtbPaotang = false;
      }
    },
    setAvatarId(state, id) {
      if (id && state.userInfo) {
        state.userInfo.contentId = id;
      }
    },
    setClinicList(state, data) {
      state.clinicList = data;
    },
    setDoctorList(state, data) {
      state.doctorList = data;
    },
    setUserList(state, data) {
      state.userList = data;
    },
    setUserListIncludDeleted(state, data) {
      state.userListIncludDeleted = data;
    },
    setPaymentMethodList(state, data) {
      state.paymentMethodList = data;
    },
    setPaymentMethodCategories(state, data) {
      state.paymentMethodCategories = data;
    },
    setLastCheckTime(state, timestamp) {
      state.lastCheckTime = timestamp;
    },
    setIsCheckingLogin(state, status) {
      state.isCheckingLogin = status;
    },
    setCheckLoginPromise(state, promise) {
      state.checkLoginPromise = promise;
    },
    setPermissionContext(state, ctx) {
      state.permissionContext = ctx || null;
    },
    setPermissionContextClinicUrl(state, url) {
      state.permissionContextClinicUrl = url || null;
    },
    // Spec 09 §5.3: the forced PIN-setup screen keys off hasPin, so setting a
    // PIN has to update it immediately rather than waiting for the next
    // /auth/context — otherwise the user sets a PIN and the screen reappears.
    setHasPin(state, value) {
      if (!state.permissionContext) return;
      state.permissionContext = {
        ...state.permissionContext,
        hasPin: Boolean(value),
      };
    },
    setCentralClinicCodes(state, codes) {
      state.centralClinicCodes = Array.isArray(codes) ? codes : [];
    },
    setCentralClinics(state, list) {
      state.centralClinics = Array.isArray(list) ? list : [];
    },
  },
  actions: {
    //--------- API ---------//
    fetchClinicList({ commit, state }) {
      if (state.clinicList && state.clinicList.length > 0) {
        return Promise.resolve({ data: state.clinicList });
      }
      return (
        axios
          .get(`/api/user/clinic`)
          .then(res => {
            commit("setClinicList", res.data);
            return res;
          })
          // Error handling is now automatic via axios interceptor
          // No need for manual eventBus emissions
          .catch(err => {
            // Store can handle business logic errors if needed
            throw err;
          })
      );
    },
    updateProfile({ commit }, payload) {
      // อัพเดทโปรไฟล์
      // Payload can include: uid, firstNameTh, lastNameTh, firstNameEn, lastNameEn,
      // mobile, drLicenseNo, drSpecialItems, gender, titleTh, titleEn
      return axios
        .put(`/api/user/userInfo`, payload)
        .then(res => {
          return res;
        })
        .catch(err => {
          // Automatic error handling via interceptor
          throw err;
        });
    },
    changePassword({ commit }, payload) {
      // แก้ไขรหัสผ่าน
      return (
        axios
          .put(`/api/user/changePassword`, payload)
          // .put("/api/user/changePassword", payload)
          .then(res => {
            return res;
          })
          .catch(err => {
            // Automatic error handling via interceptor
            throw err;
          })
      );
    },

    //--------- MFA (two-factor authentication) ---------//
    getMfaStatus({ commit }) {
      // สถานะการยืนยันตัวตนสองชั้นของผู้ใช้
      // => { enrolled, activatedDt, remainingRecoveryCodes, trustedDevices: [...] }
      return axios
        .get(`/api/user/mfa`)
        .then(res => res.data)
        .catch(err => {
          throw err;
        });
    },
    mfaSetup({ commit }, password) {
      // เริ่มลงทะเบียน TOTP (step-up ด้วยรหัสผ่าน)
      // => { otpauthUri, secretBase32, qrDataUrl }
      return axios
        .post(`/api/user/mfa/totp/setup`, { password })
        .then(res => res.data)
        .catch(err => {
          throw err;
        });
    },
    mfaActivate({ commit }, code) {
      // ยืนยันรหัส 6 หลักเพื่อเปิดใช้งาน => { recoveryCodes: [...] }
      return axios
        .post(`/api/user/mfa/totp/activate`, { code })
        .then(res => res.data)
        .catch(err => {
          throw err;
        });
    },
    mfaDisable({ commit }, { password, code }) {
      // ปิดใช้งาน TOTP (ต้องใช้รหัสผ่าน + รหัส 6 หลัก)
      return axios
        .delete(`/api/user/mfa/totp`, { data: { password, code } })
        .then(res => res.data)
        .catch(err => {
          throw err;
        });
    },
    mfaRegenerate({ commit }, { password, code }) {
      // สร้างรหัสสำรองใหม่ (ต้องใช้รหัสผ่าน + รหัส 6 หลัก) => { recoveryCodes: [...] }
      return axios
        .post(`/api/user/mfa/recoveryCodes/regenerate`, { password, code })
        .then(res => res.data)
        .catch(err => {
          throw err;
        });
    },
    revokeTrustedDevice({ commit }, id) {
      // เพิกถอนอุปกรณ์ที่เชื่อถือ
      return axios
        .delete(`/api/user/mfa/trustedDevices/${id}`)
        .then(res => res.data)
        .catch(err => {
          throw err;
        });
    },
    passkeyRegisterStart({ commit }, password) {
      // เริ่มลงทะเบียน Passkey (step-up ด้วยรหัสผ่าน) => { options, registrationToken }
      return axios
        .post(`/api/user/mfa/passkey/register/start`, { password })
        .then(res => res.data)
        .catch(err => {
          throw err;
        });
    },
    passkeyRegisterFinish({ commit }, { response, registrationToken, nickname }) {
      // ยืนยันผลลัพธ์การลงทะเบียน Passkey => { success:true, passkey:{id,nickname,lastUsedDt} }
      return axios
        .post(`/api/user/mfa/passkey/register/finish`, {
          response,
          registrationToken,
          nickname
        })
        .then(res => res.data)
        .catch(err => {
          throw err;
        });
    },
    revokePasskey({ commit }, id) {
      // เพิกถอน Passkey
      return axios
        .delete(`/api/user/mfa/passkey/${id}`)
        .then(res => res.data)
        .catch(err => {
          throw err;
        });
    },
    fetchUserInfo({ commit }) {
      return axios
        .get(`/api/user/userInfo`)
        .then(res => {
          commit("setUserInfo", res.data);
          return res;
        })
        .catch(err => {
          // Automatic error handling via interceptor
          throw err;
        });
    },
    login({ commit }, payload) {
      return axios.post("/api/user/login", payload).then(res => {
        return res;
      });
    },
    logout({ commit, dispatch }) {
      return axios
        .post(`/api/logout`)
        .then(res => {
          // return axios.post("/api/user/logout").then(res => {
          commit("setUserInfo", null);
          commit("setBranchInfo", null);
          // Also clear branch store
          dispatch("moduleBranch/setBranchInfo", null, { root: true });
          // Clear tokens from localStorage
          localStorage.removeItem("access_token");
          return res;
        })
        .catch(err => {
          // Automatic error handling via interceptor
          throw err;
        });
    },
    fetchUserLogin({ commit }) {
      return new Promise((resolve, reject) => {
        axios
          .get(`/api/user/checkLogin`)
          .then(res => {
            commit("setUserInfo", res.data.user);
            commit("setLastCheckTime", Date.now());
            resolve(res);
          })
          .catch(err => {
            reject(err);
          });
      });
    },
    checkUserLogin({ commit, dispatch, rootGetters }, payload) {
      return new Promise((resolve, reject) => {
        var endPoint = `/user/checkLogin`;
        if (payload && payload.clinicUrl && payload.branchUrl) {
          endPoint = `/user/checkLogin/${payload.clinicUrl}/${payload.branchUrl}`;
        }
        axios
          .get(endPoint)
          .then(res => {
            commit("setUserInfo", res.data.user);
            commit("setBranchInfo", res.data.branch);
            // Also dispatch to branch store to handle hasSubscribed
            dispatch("moduleBranch/setBranchInfo", res.data.branch, {
              root: true,
            });
            commit("setLastCheckTime", Date.now());
            resolve(res);
          })
          .catch(err => {
            reject(err);
          });
      });
    },
    newCheckUserLogin({ commit, dispatch, state }, payload) {
      return new Promise((resolve, reject) => {
        const now = Date.now();
        const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
        const MIN_REQUEST_INTERVAL = 30 * 1000; // 30 seconds minimum between requests

        // Check if data is still fresh (within cache duration) and complete
        if (
          state.lastCheckTime &&
          state.userInfo &&
          state.branchInfo &&
          state.userInfo.uid && // Ensure user data is complete
          state.branchInfo.id && // Ensure branch data is complete
          now - state.lastCheckTime < CACHE_DURATION
        ) {
          resolve({ data: { user: state.userInfo, branch: state.branchInfo } });
          return;
        }

        // Check if request is too frequent (rate limiting)
        if (
          state.lastCheckTime &&
          now - state.lastCheckTime < MIN_REQUEST_INTERVAL
        ) {
          resolve({ data: { user: state.userInfo, branch: state.branchInfo } });
          return;
        }

        // Check if request is already in progress (deduplication)
        if (state.isCheckingLogin && state.checkLoginPromise) {
          state.checkLoginPromise.then(resolve).catch(reject);
          return;
        }

        const endPoint =
          payload && payload.clinicUrl && payload.branchUrl
            ? `/api/user/checkLogin/${payload.clinicUrl}/${payload.branchUrl}`
            : `/api/user/checkLogin`;

        // Mark as checking and store promise for deduplication
        commit("setIsCheckingLogin", true);

        const promise = axios
          .get(endPoint)
          .then(res => {
            // Validate response data - be more flexible about structure
            if (!res.data) {
              throw new Error("No data in login check response");
            }

            // Check if response has user and branch data
            const userData = res.data.user || res.data;
            const branchData = res.data.branch || null;

            if (!userData) {
              throw new Error("No user data in login check response");
            }

            commit("setUserInfo", userData);
            if (branchData) {
              commit("setBranchInfo", branchData);
              // Also dispatch to branch store to handle hasSubscribed
              dispatch("moduleBranch/setBranchInfo", branchData, {
                root: true,
              });
            }
            commit("setLastCheckTime", now);
            commit("setIsCheckingLogin", false);
            commit("setCheckLoginPromise", null);

            // Phase 1C: load permission context in background. We don't await —
            // it should not block login. Pages can still call $can; if context
            // is not yet loaded, $can returns false (safe default) and the
            // 403 handler will refresh it on demand.
            const clinicURL = branchData?.clinic?.code;
            // eslint-disable-next-line no-console
            console.log("[clinic-roles debug] login: clinicURL for permission context =", clinicURL);
            if (clinicURL) {
              dispatch("loadPermissionContext", { clinicURL }).catch(err => {
                // eslint-disable-next-line no-console
                console.warn("loadPermissionContext failed", err?.message || err);
              });
            }

            resolve(res);
            return res;
          })
          .catch(err => {
            // Clear state on error
            commit("setUserInfo", null);
            commit("setBranchInfo", null);
            dispatch("moduleBranch/setBranchInfo", null, { root: true });
            commit("setIsCheckingLogin", false);
            commit("setCheckLoginPromise", null);
            reject(err);
          });

        commit("setCheckLoginPromise", promise);
      });
    },
    async registerUser({ commit }, payload) {
      // Payload can include: email, password, firstNameTh, lastNameTh, firstNameEn, lastNameEn,
      // mobile, userTypeId, drLicenseNo, drSpecialItems, gender, titleTh, titleEn
      return axios.post(`/api/user/register/`, payload).then(res => {
        return res;
      });
    },
    checkUserActivate({ commit }, payload) {
      return axios.post(`/api/activate/`, payload).then(res => {
        return res;
      });
    },
    checkResetPasswordKey({ commit }, payload) {
      return axios
        .post(`/api/user/forgotPassword/verify`, payload)
        .then(res => {
          // return axios.post(`/api/user/getResetPassword/`, payload).then(res => {
          return res;
        })
        .catch(err => {
          // Automatic error handling via interceptor
          throw err;
        });
    },
    resetPassword({ commit }, payload) {
      return axios
        .post(`/api/user/resetPassword/`, payload)
        .then(res => {
          return res;
        })
        .catch(err => {
          // Automatic error handling via interceptor
          throw err;
        });
    },
    forgotPassword({ commit }, payload) {
      return axios
        .post(`/api/user/forgotPassword/`, payload)
        .then(res => {
          return res;
        })
        .catch(err => {
          // Automatic error handling via interceptor
          throw err;
        });
    },
    fetchDoctorList({ commit }, payload) {
      return axios
        .get(`/api/user/${payload.clinicUrl}/${payload.branchUrl}`, {
          params: { roleIds: "2" },
        })
        .then(res => {
          commit("setDoctorList", res.data);
          return res;
        })
        .catch(err => {
          // Automatic error handling via interceptor
          throw err;
        });
    },
    fetchUserList({ commit }, payload) {
      return axios
        .get(`/api/user/${payload.clinicUrl}/${payload.branchUrl}`, {
          params: { roleIds: "1,2,3,4,5,6,7" },
        })
        .then(res => {
          commit("setUserList", res.data);
          return res;
        })
        .catch(err => {
          // Automatic error handling via interceptor
          throw err;
        });
    },
    fetchUserListIncludeDeleted({ commit }, payload) {
      return axios
        .get(`/api/user/${payload.clinicUrl}/${payload.branchUrl}`, {
          params: { roleIds: "1,2,3,4,5,6,7", activeMembersOnly: false },
        })
        .then(res => {
          commit("setUserListIncludDeleted", res.data);
          return res;
        })
        .catch(err => {
          // Automatic error handling via interceptor
          throw err;
        });
    },
    async fetchAllBranchMembers({ commit }, payload) {
      let res = await axios.get(
        `/api/user/${payload.clinicUrl}/${payload.branchUrl}`,
        { params: { roleIds: "1,2,3,4,5,6,7", activeMembersOnly: false } }
      );

      commit("setUserListIncludDeleted", res.data);

      let users = res.data.filter(i => i.deletedAt === null);
      commit("setUserList", users);

      let doctors = users.filter(i => i.authIdItems.includes(2));
      commit("setDoctorList", doctors);

      return res;
    },

    fetchPaymentMethodList({ commit, getters }, payload) {
      const params = {};
      if (payload.allBranches) params.allBranches = "true";
      return axios
        .get(
          `/api/master/mReceiptPayment/${payload.clinicUrl}/${payload.branchUrl}`,
          { params }
        )
        .then(res => {
          let list = getters.getFormatPaymentMethodList(res.data.result);
          commit("setPaymentMethodList", list);
          commit("setPaymentMethodCategories", res.data.result);
          return res;
        })
        .catch(err => {
          // Automatic error handling via interceptor
          throw err;
        });
    },

    verifyUser({ commit }, payload) {
      return axios
        .post(`/api/branch/verify/${payload.clinicUrl}/${payload.branchUrl}`, {
          username: payload.username,
          password: payload.password,
        })
        .then(res => {
          return res;
        })
        .catch(err => {
          // Automatic error handling via interceptor
          throw err;
        });
    },
    verifyLicense({ commit }, payload) {
      return axios
        .post(
          `/api/user/tdc/verify/${payload.clinicUrl}/${payload.branchUrl}`,
          {
            userId: payload.uid,
          }
        )
        .then(response => {
          // You might want to commit some mutation here
          // commit('SET_LICENSE_STATUS', response.data);
          return response.data; // Return the actual data
        })
        .catch(error => {
          // Automatic error handling via interceptor
          throw error;
        });
    },

    // Phase 1C/1.5: load permission context (scopes + roleIds per scope) for $can checks.
    async loadPermissionContext({ commit }, payload = {}) {
      const clinicURL =
        payload.clinicURL || payload.clinicCode || payload.clinicUrl;
      if (!clinicURL) {
        commit("setPermissionContext", null);
        commit("setPermissionContextClinicUrl", null);
        return null;
      }
      const res = await axios.get(`/api/auth/context`, {
        params: { clinicURL },
      });
      commit("setPermissionContext", res.data);
      commit("setPermissionContextClinicUrl", clinicURL);
      return res.data;
    },
    // Phase 1.5: load context for a clinic only if it isn't already the active one,
    // so switching clinics refreshes scopes instead of carrying stale permissions.
    async ensurePermissionContextFor({ state, dispatch }, payload = {}) {
      const clinicURL =
        payload.clinicURL || payload.clinicCode || payload.clinicUrl;
      if (!clinicURL) return state.permissionContext;
      if (
        state.permissionContext &&
        state.permissionContextClinicUrl === clinicURL
      ) {
        return state.permissionContext;
      }
      return dispatch("loadPermissionContext", { clinicURL });
    },
    // Idle screen lock (spec 09). None of these mint a token or change the
    // session — a successful unlock only tells the app it may drop its overlay.
    async unlockWithPin(_, pin) {
      const res = await axios.post(`/api/user/pin/verify`, { pin });
      return res.data;
    },
    async unlockWithPassword(_, password) {
      const res = await axios.post(`/api/user/lock/unlock`, { password });
      return res.data;
    },
    // payload: {pin, currentPin?, password?}. currentPin or password is
    // required only when the user already has a PIN.
    async setOwnPin({ commit }, payload) {
      await axios.post(`/api/user/pin`, payload);
      commit("setHasPin", true);
    },
    // Fast user switching (spec 11). No device id anywhere: the browser
    // attaches the httpOnly dc_device cookie automatically, and the frontend
    // can neither read nor forge it.
    async loadDeviceSessions() {
      const res = await axios.get(`/api/device/sessions`);
      return res.data.sessions || [];
    },
    async switchDeviceUser(_, { uid, pin }) {
      const res = await axios.post(`/api/device/session/switch`, { uid, pin });
      return res.data;
    },
    async endShift() {
      const res = await axios.post(`/api/device/session/end-shift`, {});
      return res.data;
    },
    // Phase 1.5: clinics where the user holds a central role (incl. users with no
    // branch membership). One call to the dedicated endpoint — no per-clinic probing.
    async loadCentralClinics({ commit }) {
      try {
        const res = await axios.get(`/api/user/central-clinics`);
        const list = Array.isArray(res.data) ? res.data : [];
        commit("setCentralClinics", list);
        commit("setCentralClinicCodes", list.map(c => c.code));
        return list;
      } catch (e) {
        commit("setCentralClinics", []);
        commit("setCentralClinicCodes", []);
        return [];
      }
    },

    //--------- State ---------//
    setStateBranchInfo({ commit }, data) {
      commit("setBranchInfo", data);
    },
    setStateAvatarId({ commit }, id) {
      commit("setAvatarId", id);
    },
  },
  getters: {
    findBy: () => (dataList, value, key, defaultVal) => {
      if (!value) {
        return defaultVal;
      }
      return (
        dataList.find(function (item) {
          return item[key] == value;
        }) || defaultVal
      );
    },
    getUserInfo: state => {
      return state.userInfo;
    },
    // Idle screen lock (spec 09 §6). Both ride on /auth/context, so they are
    // per-clinic and refresh for free when the user switches clinic.
    getIdleLockMinutes: state => {
      const value = state.permissionContext?.idleLockMinutes;
      return typeof value === "number" ? value : null;
    },
    getHasPin: state => Boolean(state.permissionContext?.hasPin),
    getUserSwitchingEnabled: state =>
      Boolean(state.permissionContext?.userSwitchingEnabled),
    // The forced-setup condition: the clinic wants the lock and this user has
    // nothing to unlock it with yet.
    getNeedsPinSetup: state =>
      typeof state.permissionContext?.idleLockMinutes === "number" &&
      !state.permissionContext?.hasPin,
    getBranchInfo: state => {
      if (state.branchInfo) {
        state.branchInfo.confApptDuration = isDate(
          state.branchInfo.confApptDuration
        )
          ? moment(state.branchInfo.confApptDuration).format("HH:mm")
          : state.branchInfo.confApptDuration || "00:15"; //setting time period
        state.branchInfo.confOpeningTime = isDate(
          state.branchInfo.confOpeningTime
        )
          ? moment(state.branchInfo.confOpeningTime).format("HH:mm")
          : state.branchInfo.confOpeningTime || "09:00"; //setting  opening time
        state.branchInfo.confClosingTime = isDate(
          state.branchInfo.confClosingTime
        )
          ? moment(state.branchInfo.confClosingTime).format("HH:mm")
          : state.branchInfo.confClosingTime || "20:00"; //setting closing time
      }
      return state.branchInfo;
    },
    getClinicInfo: state => {
      if (state.branchInfo && state.branchInfo.clinic) {
        return state.branchInfo.clinic;
      } else {
        return {};
      }
    },
    getRoomList: state => {
      if (state.branchInfo && state.branchInfo.rooms) {
        return state.branchInfo.rooms.map(room => {
          room.title = room.name;
          return room;
        });
      } else {
        return [];
      }
    },
    getRoomListBy: (state, getters) => (value, key) => {
      return getters.findBy(state.branchInfo.rooms, value, key, {
        value: null,
        label: "-",
      });
    },
    getAuthorization: state => {
      if (state.branchInfo && state.branchInfo.auth) {
        return state.branchInfo.auth;
      } else {
        return { authId: 0 };
      }
    },
    getBranchPath: state => {
      return state.branchPath;
    },
    getClinicList: state => {
      return state.clinicList;
    },
    // getDoctorList: (state) => {
    //     return state.doctorList;
    // },
    getDoctorList: state => {
      if (!state.doctorList || !Array.isArray(state.doctorList)) {
        return [];
      }
      let list = state.doctorList.sort((i, j) =>
        i.fullName.localeCompare(j.label)
      );
      return list;
    },
    getDoctorListBy: (state, getters) => (value, key) => {
      return getters.findBy(state.doctorList, value, key, {
        value: null,
        label: "-",
      });
    },
    getUserList: state => {
      let list = state.userList.sort((i, j) =>
        i.fullName.localeCompare(j.fullName)
      );
      return list;
    },
    getUserListBy: (state, getters) => (value, key) => {
      return getters.findBy(state.userList, value, key, {
        value: null,
        label: "-",
      });
    },
    getUserListIncludeDeletedBy: (state, getters) => (value, key) => {
      return getters.findBy(state.userListIncludDeleted, value, key, {
        value: null,
        label: "-",
      });
    },
    getPaymentMethodList: state => {
      return state.paymentMethodList;
    },
    getPaymentMethodCategories: state => {
      return state.paymentMethodCategories;
    },
    hasDepositMethods: state => {
      const category = state.paymentMethodCategories.find(
        cat => cat.type === "Deposit"
      );
      return (category?.mReceiptPayment?.filter(m => !m.del) || []).length > 0;
    },
    getFormatPaymentMethodList: (state, getters) => data => {
      let list = [];
      data.forEach(i => {
        list.push(...i.mReceiptPayment);
      });
      return list;
    },

    isManager: state => {
      return state.branchInfo.auth.authIdItems.includes(1);
    },
    isDoctor: state => {
      return state.branchInfo.auth.authIdItems.includes(2);
    },
    isCounter: state => {
      return state.branchInfo.auth.authIdItems.includes(3);
    },
    isAssistant: state => {
      return state.branchInfo.auth.authIdItems.includes(4);
    },
    isHeadCounter: state => {
      return state.branchInfo.auth.authIdItems.includes(5);
    },
    isStockManager: state => {
      return state.branchInfo.auth.authIdItems.includes(6);
    },
    isStockStaff: state => {
      return state.branchInfo.auth.authIdItems.includes(7);
    },
    getIsAccessMenu:
      state =>
      (roles = []) => {
        const roleList = {
          MANAGER: 1,
          DOCTOR: 2,
          COUNTER: 3,
          ASSISTANT: 4,
          HEADCOUNTER: 5,
          ROLE_BRANCH_STOCK_MANAGER: 6,
          ROLE_STOCK_STAFF: 7,
        };
        roles = roles.map(role => roleList[String(role).toUpperCase()]);
        if (state.branchInfo && state.branchInfo.auth) {
          return state.branchInfo.auth.authIdItems.some(r => roles.includes(r));
        } else {
          return false;
        }
      },
    getLinkedWithKtbPaotang: state => {
      return state.branchInfo.linkedWithKtbPaotang;
    },
    getLinkedWithNhso: state => {
      return state.branchInfo.linkedWithNhso;
    },
    getNhsoSupportedItems: state => {
      return state.branchInfo.nhsoSubitemNoSupportedList;
    },

    // Phase 1C: permission-context getters.
    getPermissionContext: state => state.permissionContext,
    getPermissionContextClinicUrl: state => state.permissionContextClinicUrl,
    getCentralClinicCodes: state => state.centralClinicCodes,
    getCentralClinics: state => state.centralClinics,

    // can(permKey, ctx) — checks BE permission against current context.
    // ctx: { clinicId?, branchId? }. If not given, returns true if any scope matches.
    can: state => (permKey, ctx = {}) => {
      const allowed = PERMISSION_MAP[permKey];
      if (!allowed || allowed.length === 0) return false;
      if (!state.permissionContext || !Array.isArray(state.permissionContext.scopes)) {
        return false;
      }
      for (const scope of state.permissionContext.scopes) {
        if (ctx.clinicId !== undefined && scope.clinicId !== ctx.clinicId) continue;
        const roleIds = scope.roleIds || [];
        const hasRole = roleIds.some(id => allowed.includes(id));
        if (!hasRole) continue;

        if (scope.type === "branch") {
          if (ctx.branchId === undefined || scope.branchId === ctx.branchId) return true;
        } else if (scope.type === "clinic_wide") {
          return true;
        } else if (scope.type === "branch_subset") {
          const branchIds = scope.branchIds || [];
          if (ctx.branchId === undefined || branchIds.includes(ctx.branchId)) return true;
        }
      }
      return false;
    },

    // assignableRoles(targetScope) — list of role keys the actor can assign.
    // targetScope: 'branch' (default) | 'clinic' | 'all'
    assignableRoles: state => (targetScope = "branch") => {
      if (!state.permissionContext || !Array.isArray(state.permissionContext.scopes)) {
        return [];
      }
      const actorKeys = new Set();
      for (const scope of state.permissionContext.scopes) {
        for (const id of scope.roleIds || []) {
          const key = ROLE_KEYS[id];
          if (key) actorKeys.add(key);
        }
      }
      const targets = new Set();
      for (const k of actorKeys) {
        (ASSIGNABLE[k] || []).forEach(t => targets.add(t));
      }
      let arr = [...targets];
      if (targetScope === "branch") {
        arr = arr.filter(k => BRANCH_ROLE_KEYS.includes(k));
      } else if (targetScope === "clinic") {
        arr = arr.filter(k => CENTRAL_ROLE_KEYS.includes(k));
      }
      return arr;
    },

    // hasAnyCentralRole — มี central role อะไรก็ได้ในคลินิกที่โหลด context ไว้
    // รวม ACCOUNTANT / PROCUREMENT ที่เข้าหน้า central ไม่ได้จริง จึง "ห้าม" ใช้ตัวนี้
    // คุมลิงก์เข้า central — ใช้ can() ของหน้าปลายทางแทน (ดู Sidebar.canEnterCentral)
    hasAnyCentralRole: state => {
      if (!state.permissionContext || !Array.isArray(state.permissionContext.scopes)) {
        return false;
      }
      return state.permissionContext.scopes.some(s =>
        s.type === "clinic_wide" || s.type === "branch_subset"
      );
    },

    // isClinicWideDashboardViewer — เห็นภาพรวม "ทั้งคลินิก" จริง ๆ (C_LEVEL) ไม่ใช่แค่บางสาขา
    // ใช้ลำดับเดียวกับ authByClinicURL ฝั่ง BE ที่ให้ clinic_wide ชนะ branch_subset
    // คนที่เป็นทั้ง C_LEVEL และ RDM จึงได้ branchIds = null และเห็นคำว่า "ภาพรวมคลินิก"
    // ตรงกับข้อมูลที่ได้จริง
    // คืน false ระหว่าง context ยังไม่โหลด — ปลอดภัยเพราะเมนู/หน้าที่ใช้ค่านี้ถูกซ่อน
    // ด้วย can() ที่อ่าน context ชุดเดียวกันอยู่แล้ว
    isClinicWideDashboardViewer: state => {
      const allowed = PERMISSION_MAP[PERMISSIONS.DASHBOARD_CLINIC_VIEW];
      const scopes = state.permissionContext && state.permissionContext.scopes;
      if (!Array.isArray(scopes)) return false;
      return scopes.some(
        s => s.type === "clinic_wide" && (s.roleIds || []).some(id => allowed.includes(id))
      );
    },
  },
};
export default moduleUser;
